
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDebugDate } from '@/contexts/DebugDateContext';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { clearGameState } from '@/components/game/GamePersistenceV2';
import { validateDebugMode, setDebugDate as setDebugDateUtil } from '@/lib/debugUtils';
import { useCallback, memo, useEffect, useState } from 'react';
import { normalizeDate, getEffectiveDate, simulateMidnight } from '@/lib/dateUtils';
import { timeoutManager } from '@/lib/managers/TimeoutManager';
import { useGameMode } from '@/contexts/GameModeContext';

export const DebugDateOverlay = memo(() => {
  const { debugDate, setDebugDate, isDebugMode } = useDebugDate();
  const { currentMode } = useGameMode();
  const [isChangingDate, setIsChangingDate] = useState(false);

  // Early return if debug mode is not active
  if (!isDebugMode) {
    console.log('ℹ️ Debug mode is not active. Add ?debug=date to URL to enable debug mode.');
    return null;
  }

  // Safety check for debug date
  if (!debugDate) {
    console.error('❌ Debug mode inconsistency: isDebugMode is true but debugDate is null');
    return null;
  }

  // Validate debug mode state
  const validation = validateDebugMode();
  console.log('🔍 Debug overlay validation:', {
    currentDebugDate: debugDate.toISOString(),
    effectiveDate: getEffectiveDate().toISOString(), // Add effective date for comparison
    formattedDate: format(debugDate, 'yyyy-MM-dd'),
    validation,
    timestamp: new Date().toISOString()
  });

  const handleDateChange = useCallback(async (direction: 'prev' | 'next') => {
    // Prevent rapid clicking
    if (isChangingDate) {
      console.log('⚠️ Date change already in progress, ignoring request');
      return;
    }

    setIsChangingDate(true);
    
    try {
      // ATOMIC BLOCK 1: Calculate new date
      const newDate = new Date(debugDate);
      if (direction === 'prev') {
        newDate.setDate(newDate.getDate() - 1);
      } else {
        newDate.setDate(newDate.getDate() + 1);
      }

      // Normalize the new date
      const normalizedDate = normalizeDate(newDate);

      console.log('🔄 DEBUG - Date change initiated:', {
        from: debugDate.toISOString(),
        to: normalizedDate.toISOString(),
        direction,
        timestamp: new Date().toISOString()
      });
      
      // ATOMIC BLOCK 2: First update debug date in storage
      // This ensures the date is available to any code that needs it
      try {
        // Guard: block persistence during debug date change to prevent cross-day stamping
        localStorage.setItem('babblegram_debug_date_change_in_progress', 'true');
      } catch {}
      setDebugDateUtil(normalizedDate);
      
      // ATOMIC BLOCK 3: Clear DAILY game state explicitly regardless of current UI mode
      // This prevents daily completion/status from leaking into the next day during debug jumps
      try {
        console.log('🔄 DEBUG - Clearing DAILY game state before date change (explicit)');
        const { modeAwareStorage } = await import('@/lib/managers/ModeAwareStorageManager');
        modeAwareStorage.clearModeStateForMode('daily');
      } catch (e) {
        console.error('Failed to clear DAILY mode state explicitly:', e);
      }
      
      // Preserve practice progress when testing daily mode
      if (currentMode !== 'daily') {
        console.log('🔄 DEBUG - Skipping PRACTICE/CUSTOM clear (current mode is', currentMode, ')');
      }
      
      // ATOMIC BLOCK 4: Update debug date in context
      // Now that we've cleared state, update the context
      setDebugDate(normalizedDate);
      

      // ATOMIC BLOCK 5: Force a page reload with debug param
      // Clear any existing reload timeout first
      timeoutManager.clearTimeout('debugDateReload');
      
      // Use timeout to ensure state changes are committed before reload
      console.log('🔄 DEBUG - Preparing for page reload with new date:', normalizedDate.toISOString());
      timeoutManager.setTimeout('debugDateReload', () => {
        console.log('🔄 DEBUG - Reloading page with debug=date param');
        window.location.href = `${window.location.pathname}?debug=date`;
      }, 500);
    } catch (error) {
      console.error('Error changing debug date:', error);
      toast.error('Failed to change debug date');
      setIsChangingDate(false);
    }
  }, [debugDate, setDebugDate, isChangingDate, currentMode]);

  const handleSimulateMidnight = useCallback(() => {
    if (currentMode !== 'daily') {
      toast.error('Midnight simulation only works in daily mode');
      return;
    }
    
    toast.success('🔥 Simulating midnight in 5 seconds...');
    simulateMidnight();
  }, [currentMode]);

  const handleGoToToday = useCallback(async () => {
    if (isChangingDate) {
      return;
    }

    setIsChangingDate(true);
    
    try {
      const today = normalizeDate(new Date());
      
      console.log('🔄 DEBUG - Going to today:', {
        from: debugDate.toISOString(),
        to: today.toISOString(),
        timestamp: new Date().toISOString()
      });
      
      // Guard: block persistence during debug date change
      try {
        localStorage.setItem('babblegram_debug_date_change_in_progress', 'true');
      } catch {}
      setDebugDateUtil(today);
      
      // Clear DAILY game state
      try {
        console.log('🔄 DEBUG - Clearing DAILY game state before going to today');
        const { modeAwareStorage } = await import('@/lib/managers/ModeAwareStorageManager');
        modeAwareStorage.clearModeStateForMode('daily');
      } catch (e) {
        console.error('Failed to clear DAILY mode state:', e);
      }
      
      // Update debug date in context
      setDebugDate(today);
      
      // Force page reload
      timeoutManager.clearTimeout('debugDateReload');
      console.log('🔄 DEBUG - Reloading page for today:', today.toISOString());
      timeoutManager.setTimeout('debugDateReload', () => {
        window.location.href = `${window.location.pathname}?debug=date`;
      }, 500);
    } catch (error) {
      console.error('Error going to today:', error);
      toast.error('Failed to go to today');
      setIsChangingDate(false);
    }
  }, [debugDate, setDebugDate, isChangingDate]);

  return (
    <div className="fixed top-0 left-0 right-0 bg-gray-900 text-white p-2 flex justify-center items-center space-x-4 z-[99999]">
      <div className="absolute left-2 flex items-center space-x-2">
        <div className="bg-yellow-500 px-2 py-0.5 rounded text-xs font-mono">
          DEBUG MODE
        </div>
        <div className="text-xs text-gray-400">
          🎯 Game state will reset on date change
        </div>
      </div>
      <button
        onClick={() => handleDateChange('prev')}
        disabled={isChangingDate}
        className={`p-1 rounded ${isChangingDate ? 'bg-gray-700 opacity-50 cursor-not-allowed' : 'hover:bg-gray-700'}`}
        aria-label="Previous day"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <span className="font-mono">
        {format(debugDate, 'yyyy-MM-dd')}
      </span>
      <button
        onClick={() => handleDateChange('next')}
        disabled={isChangingDate}
        className={`p-1 rounded ${isChangingDate ? 'bg-gray-700 opacity-50 cursor-not-allowed' : 'hover:bg-gray-700'}`}
        aria-label="Next day"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      <button
        onClick={handleGoToToday}
        disabled={isChangingDate}
        className={`px-3 py-1 rounded text-xs font-mono ${
          isChangingDate 
            ? 'bg-gray-700 opacity-50 cursor-not-allowed' 
            : 'bg-blue-600 hover:bg-blue-700'
        }`}
        title="Go to today's date"
      >
        📅 Today
      </button>
      <div className="absolute right-2">
        <button
          onClick={handleSimulateMidnight}
          disabled={isChangingDate || currentMode !== 'daily'}
          className={`px-3 py-1 rounded text-xs font-mono ${
            currentMode !== 'daily' || isChangingDate
              ? 'bg-gray-700 opacity-50 cursor-not-allowed' 
              : 'bg-red-600 hover:bg-red-700'
          }`}
          title={currentMode !== 'daily' ? 'Only available in daily mode' : 'Simulate midnight transition in 5 seconds'}
        >
          🕛 Simulate Midnight
        </button>
      </div>
    </div>
  );
});

DebugDateOverlay.displayName = 'DebugDateOverlay';
